/**
 * The library-match pieces of the Review card's combined reply (see {@link ./reviewReply}):
 * how a pick is worded for the agent, and how that wording is read back (live click,
 * replay, second tab) so the resolved card never needs state beyond the answer text.
 */

import type { AvSelectionCandidate, AvSelectionCheckpoint } from '../types/epi-chat-types';
import type { VariableMatchSelection } from '../types/variable-match-types';

const BUILD_PHRASE = 'Build new instead — no variable match selected.';

export const matchRequestedLabel = (checkpoint: AvSelectionCheckpoint): string =>
  checkpoint.requestedVariable ?? checkpoint.summary;

export const matchVerdict = (selection: VariableMatchSelection | undefined): string =>
  !selection || selection.type === 'build'
    ? BUILD_PHRASE
    : `I'll use "${selection.candidate.dataElement}" (alias: ${selection.candidate.name}).`;

/**
 * Read a selection back from its verdict. A verdict that matches no candidate
 * reads as `build`, so a resolved card never claims a variable was reused when
 * it cannot be shown.
 */
export const parseMatchVerdict = (
  verdict: string,
  checkpoint: AvSelectionCheckpoint
): VariableMatchSelection => {
  if (verdict.includes(BUILD_PHRASE)) return { type: 'build' };
  const chosen = [checkpoint.primary, ...checkpoint.alternatives]
    .filter((c): c is AvSelectionCandidate => c !== null)
    .find((c) => verdict.includes(`"${c.dataElement}"`));
  return chosen ? { type: 'use', candidate: chosen } : { type: 'build' };
};
